import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "djspy_cart_v1";
const PACKS_KEY = "djspy_cart_packs_v1";

function readPacks() {
  try {
    const parsed = JSON.parse(localStorage.getItem(PACKS_KEY) || "[]");
    return Array.isArray(parsed) ? parsed.filter((x) => x && typeof x.id === "string") : [];
  } catch {
    return [];
  }
}


const CartCtx = createContext({
  items: [],
  isOpen: false,
  addItem: () => {},
  removeItem: () => {},
  clear: () => {},
  open: () => {},
  close: () => {},
  hasId: () => false,
});

function readCart() {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Snapshot fields we display in the drawer; source of truth for price
    // is always the backend at checkout time.
    return parsed
      .filter((x) => x && typeof x.id === "string")
      .map((x) => ({
        id: x.id,
        title: x.title || "",
        artist: x.artist || "",
        price: Number(x.price || 0),
        cover_url: x.cover_url || "",
        genre: x.genre || "",
      }));
  } catch {
    return [];
  }
}

function writeCart(items) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch { /* noop */ }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => readCart());
  const [packs, setPacks] = useState(() => (typeof window === "undefined" ? [] : readPacks()));
  const [isOpen, setOpen] = useState(false);

  useEffect(() => { writeCart(items); }, [items]);
  useEffect(() => { try { localStorage.setItem(PACKS_KEY, JSON.stringify(packs)); } catch { /* noop */ } }, [packs]);

  // Cross-tab sync
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setItems(readCart());
      if (e.key === PACKS_KEY) setPacks(readPacks());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const addItem = useCallback((track) => {
    if (!track || !track.id) return false;
    // Never add tracks the user already owns
    if (track.owned) return false;
    setItems((prev) => (prev.some((x) => x.id === track.id)
      ? prev
      : [...prev, {
          id: track.id,
          title: track.title,
          artist: track.artist || "",
          price: Number(track.price || 0),
          cover_url: track.cover_url || "",
          genre: track.genre || "",
          track_type: track.track_type || null,
          edit_category: track.edit_category || null,
        }]
    ));
    return true;
  }, []);

  /**
   * Bulk-add multiple tracks in one commit.  Returns the number of
   * tracks actually added (dedupes against current cart, skips tracks
   * marked as owned or missing an id).  The homepage "Add All
   * Remaining Premium" one-click CTA uses this so we don't render 40
   * intermediate cart states.
   */
  const addMany = useCallback((tracks) => {
    if (!Array.isArray(tracks) || tracks.length === 0) return 0;
    let added = 0;
    setItems((prev) => {
      const existing = new Set(prev.map((x) => x.id));
      const next = [...prev];
      for (const t of tracks) {
        if (!t || !t.id || t.owned || existing.has(t.id)) continue;
        existing.add(t.id);
        next.push({
          id: t.id,
          title: t.title || "",
          artist: t.artist || "",
          price: Number(t.price || 0),
          cover_url: t.cover_url || "",
          genre: t.genre || "",
          track_type: t.track_type || null,
          edit_category: t.edit_category || null,
        });
        added += 1;
      }
      return added ? next : prev;
    });
    return added;
  }, []);

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const removeMany = useCallback((ids) => {
    const drop = new Set(ids || []);
    setItems((prev) => prev.filter((x) => !drop.has(x.id)));
  }, []);

  // Packs store only display snapshots; price is always recomputed server-side.
  const addPack = useCallback((pack) => {
    if (!pack || !pack.id) return false;
    setPacks((prev) => (prev.some((x) => x.id === pack.id) ? prev : [...prev, {
      id: pack.id, slug: pack.slug, name_en: pack.name_en, name_ar: pack.name_ar || "",
      artwork_url: pack.artwork_url || "", track_count: pack.track_count || 0,
      track_ids: pack.track_ids || (pack.tracks || []).map((t) => t.id),
    }]));
    return true;
  }, []);

  const removePack = useCallback((id) => setPacks((prev) => prev.filter((x) => x.id !== id)), []);

  const clear = useCallback(() => { setItems([]); setPacks([]); }, []);

  const hasId = useCallback((id) => items.some((x) => x.id === id), [items]);

  const value = useMemo(() => ({
    items, packs, isOpen, addItem, addMany, removeItem, removeMany, clear, addPack, removePack,
    open: () => setOpen(true), close: () => setOpen(false),
    hasId, hasPack: (id) => packs.some((x) => x.id === id),
  }), [items, packs, isOpen, addItem, addMany, removeItem, removeMany, clear, addPack, removePack, hasId]);

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export function useCart() { return useContext(CartCtx); }
